/**
 * Detects whether an auth flow was started from Agent Grid, so the auth views can
 * render Agent Grid branding instead of Anima's.
 *
 * Agent Grid reaches /login through the public API's OAuth authorize endpoint, which
 * sets `platform=agentgrid` on the login URL. We also sniff the redirect target so the
 * branding survives links that carry the authorize URL but lost the `platform` param.
 */

export const AGENT_GRID_PLATFORM = 'agentgrid';
export const CLAIM_FLOW = 'claim';

const AGENT_GRID_HOSTNAME = 'agentgrid.io';
const AGENT_GRID_PATH = '/auth/agentgrid/';
const AGENT_GRID_CLIENT_ID = 'agentgrid-web';
const URL_PARSE_BASE = 'https://animaapp.com';

const isAgentGridHostname = (hostname) => {
  const normalized = hostname?.toLowerCase();
  return normalized === AGENT_GRID_HOSTNAME || !!normalized?.endsWith(`.${AGENT_GRID_HOSTNAME}`);
};

const parseUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  try {
    // Relative redirects ("/auth/agentgrid/callback") still need a base to parse.
    return new URL(value, typeof window === 'undefined' ? URL_PARSE_BASE : window.location.origin);
  } catch (err) {
    return null;
  }
};

export const isAgentGridRedirect = (redirect) => {
  const url = parseUrl(redirect);
  if (!url) {
    return false;
  }

  if (isAgentGridHostname(url.hostname)) {
    return true;
  }

  if (url.pathname.toLowerCase().includes(AGENT_GRID_PATH)) {
    return true;
  }

  // The login redirect points at the public API authorize URL, which carries the
  // Agent Grid client id and the Agent Grid app callback as OAuth params.
  if (url.searchParams.get('client_id')?.toLowerCase() === AGENT_GRID_CLIENT_ID) {
    return true;
  }

  const redirectUri = parseUrl(url.searchParams.get('redirect_uri'));
  return !!redirectUri && isAgentGridHostname(redirectUri.hostname);
};

export const isAgentGridSource = (query) => {
  const platform = query?.platform;
  if (typeof platform === 'string' && platform.toLowerCase() === AGENT_GRID_PLATFORM) {
    return true;
  }

  return isAgentGridRedirect(query?.redirect);
};

/**
 * Product-specific auth flows can bypass Anima's first-run onboarding. Agent Grid
 * is detected independently of `passOnBoarding` so a lost query flag cannot strand
 * a newly-created user before the OAuth callback.
 */
export const shouldSkipAuthOnboarding = (query) => !!query?.passOnBoarding || isAgentGridSource(query);

const isClaimFlow = (value) => typeof value === 'string' && value.toLowerCase() === CLAIM_FLOW;

/**
 * Signing in is the claim flow's first step, so that flow marks its login link with `flow=claim`
 * and the auth views lay themselves out like the rest of it. Sniffed out of the redirect too, the
 * same way the brand is: the param has to survive the trip through the authorize endpoint.
 */
export const isAgentGridClaimFlow = (query) => {
  if (!isAgentGridSource(query)) {
    return false;
  }

  if (isClaimFlow(query?.flow)) {
    return true;
  }

  const redirect = parseUrl(query?.redirect);
  return !!redirect && isClaimFlow(redirect.searchParams.get('flow'));
};
